import type { AppProps } from 'next/app';
import React, { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { useAppConfigContext } from '@/context/AppContext';
import useOnMount from '@/hooks/useOnMount';
import { useProfileContext } from '@/context/profileContext';
import LocalStorage from '@/utils/localStorage';
import { EFL_CART_ID } from '@/constants/app';
import { useEcomProductContext } from '@/context/ecomProductContext';
import { getCart } from '@/actions/ecom/cart';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import dlv from 'dlv';
import { ECOM_CART_URL, ECOM_CHECKOUT_URL } from '@/configs/constants';
import dynamic from 'next/dynamic';
import useDeviceChange from '@/hooks/useDeviceChange';
import WebengageInit from '@/globalComponents/webengageInit';
import { getEnvValue } from '@/utils/envUtils';
import WhatsappButton from '@/components/WhatsappButton';
import Div from '@/shared/Div';
import SaleAssistButton from '@/components/SaleAssistWidget/SaleAssistButton';

import useFeatureFlags from '@/hooks/useFeatureFlags';
import classNames from 'classnames';

const GlobalSearch = React.lazy(
  () => import(/* webpackChunkName: "app-lazy-chunks" */ '@/components/Search/GlobalSearch'),
);

const HamBurgerMenu = React.lazy(
  () => import(/* webpackChunkName: "app-lazy-chunks" */ '@/shared/crisp/HamBurgerMenu'),
);

const Login = React.lazy(
  () => import(/* webpackChunkName: "app-lazy-chunks" */ '@/shared/crisp/Login'),
);

const Toast = React.lazy(
  () => import(/* webpackChunkName: "app-lazy-chunks" */ '@/globalComponents/toast'),
);

const CookieBanner = dynamic(
  () => import(/* webpackChunkName: "app-lazy-ssr-chunks" */ '@/components/Static/CookieBanner'),
  {
    ssr: false,
  },
);

const PromoOffer = dynamic(
  () => import(/* webpackChunkName: "promo-offer-chunk" */ '@/components/PromoOffer'),
  {
    ssr: false,
  },
);

const AQIBar = lazy(() => import(/* webpackChunkName: "aqi-bar-chunks" */ '@/components/AQI/Bar'));

interface LayoutProps extends AppProps {
  host?: string;
  location: string | null;
}
const Layout = ({ Component, pageProps, router, host, location }: LayoutProps) => {
  const { state: profile } = useProfileContext();
  const { isAQIEnabled } = useFeatureFlags();
  const { state: appConfig, dispatch: appConfigDispatch } = useAppConfigContext();
  const { dispatch: ecomProductDispatch } = useEcomProductContext();
  const [weKey, setWeKey] = useState('');
  useDeviceChange();
  const { search, hamburger, header, login, toast, saleAssist, isFlutterClient, isSCAClient } =
    appConfig;
  const { toggleSearch, toggleHamburger, toggleLoginModal, toggleSaleAssist, updateLocation } =
    appConfigDispatch;

  // Fetch Cart Item
  const fetchCartDetail = (...args: any) => getCart.apply(null, args);
  const cartDetail = useTriggerRequest({ fetch: fetchCartDetail });
  useOnMount(async () => {
    if (!isSCAClient) {
      const cartId = LocalStorage.get(EFL_CART_ID);
      if (
        (profile.isLoggedIn || cartId) &&
        router.pathname !== ECOM_CART_URL &&
        router.pathname !== ECOM_CHECKOUT_URL
      ) {
        const { data } = await cartDetail.promisifyTrigger(cartId, false);
        if (!data?.data) LocalStorage.remove(EFL_CART_ID);
        const items = dlv(data, 'data.items', []);
        ecomProductDispatch.updateInCartItems(items.map((item: any) => item.item.productId));
      }
    }

    setWeKey(getEnvValue(isSCAClient ? 'WEBENGAGE_KEY_SCA' : 'WEBENGAGE_KEY'));
  });

  // Sync location from _app to appConfig context
  useEffect(() => {
    if (location && location !== appConfig?.config?.location) {
      updateLocation(location);
    }
  }, [location, appConfig?.config?.location]);

  return (
    <Fragment>
      {isAQIEnabled && !isFlutterClient && !isSCAClient ? (
        <Suspense>
          <AQIBar />
        </Suspense>
      ) : null}
      <Component {...{ ...pageProps }} router={router} />
      {weKey ? <WebengageInit weKey={weKey} /> : null}
      {search ? (
        <Suspense>
          <GlobalSearch search={search} toggleSearch={toggleSearch} isSCAClient={isSCAClient} />
        </Suspense>
      ) : null}
      {hamburger ? (
        <Suspense>
          <HamBurgerMenu header={header} hamburger={hamburger} toggleHamburger={toggleHamburger} />
        </Suspense>
      ) : null}
      {!profile.isLoggedIn && login.open ? (
        <Suspense>
          <Login {...(login ?? {})} onClose={toggleLoginModal} />
        </Suspense>
      ) : null}
      {toast?.show && toast?.message ? (
        <Suspense>
          <Toast {...toast} />
        </Suspense>
      ) : null}
      <CookieBanner isFlutterClient={isFlutterClient} isSCAClient={isSCAClient} />
      {isFlutterClient || isSCAClient ? null : <PromoOffer />}
      <Div
        className={classNames(
          'fixed flex flex-col gap-s3 md:gap-s4 justify-center md:w-s9 bottom-s6 md:bottom-s8 right-s6 xl:right-[calc(((100vw-1280px)/2)+22px)] z-40',
          {
            '!bottom-[90px]': isSCAClient,
          },
        )}
      >
        <WhatsappButton
          pathname={router?.pathname}
          isFlutterClient={isFlutterClient}
          isSCAClient={isSCAClient}
        />
        <SaleAssistButton
          isFlutterClient={isFlutterClient}
          pathname={router?.pathname}
          toggleSaleAssist={toggleSaleAssist}
          saleAssist={saleAssist}
          isSCAClient={isSCAClient}
        />
      </Div>
    </Fragment>
  );
};

export default Layout;
